import { useRef, useEffect, useCallback, useMemo, useState } from 'react';
import { isGroupedData } from '../index';

export const useDragSelect = ({
  options,
  value,
  id,
  isMulti,
  isTagsStyle,
  dragSelectAcrossGroups,
  parentOnChange,
  setSearchString,
  setSearchBarFocus,
  setFocusSelectTagsInput
}) => {
  // Flatten options for index-based drag selection (handles both flat and grouped data)
  const flatOptions = useMemo(() => {
    if (isGroupedData(options)) {
      return options.flatMap(group => group.options);
    }
    return options;
  }, [options]);

  // Build a map of flat index to group index for group-confined selection
  const indexToGroupMap = useMemo(() => {
    if (!isGroupedData(options)) return null;
    const map = {};
    let flatIndex = 0;
    options.forEach((group, groupIndex) => {
      group.options.forEach(() => {
        map[flatIndex] = groupIndex;
        flatIndex++;
      });
    });
    return map;
  }, [options]);

  // Drag state ref (using ref to avoid re-renders during drag)
  const dragStateRef = useRef({
    isDragging: false,
    hasMoved: false,           // Track if cursor moved to a different item
    dragAction: null,          // 'select' or 'deselect'
    processedIndices: new Set(),
    pendingChanges: [],        // Batch changes until mouseup
    lastIndex: null,
    startIndex: null,          // The first item where drag started
    startOption: null,         // The first option object
    startGroupIndex: null      // Track starting group for confined selection
  });

  // Optimistic selections state for visual feedback during drag
  // Contains option values that are being toggled (will show flipped state)
  const [optimisticSelections, setOptimisticSelections] = useState(new Set());

  // Check if an option is currently selected
  const isOptionSelected = useCallback((option) => {
    if (!value || !Array.isArray(value)) return false;
    return value.some(v => v.value === option.value);
  }, [value]);

  // Start drag operation (on mousedown)
  const handleDragStart = useCallback((index, option, isCurrentlySelected, groupIndex = null) => {
    if (!isMulti || isTagsStyle) return;
    
    // Prepare drag state but don't toggle yet - wait to see if it's a click or drag
    dragStateRef.current = {
      isDragging: true,
      hasMoved: false,
      dragAction: isCurrentlySelected ? 'deselect' : 'select',
      processedIndices: new Set([index]),
      pendingChanges: [], // Will add first item when we confirm it's a drag
      lastIndex: index,
      startIndex: index,
      startOption: option,
      startGroupIndex: groupIndex
    };
  }, [isMulti, isTagsStyle]);

  // Handle drag over an item (with range fill for fast dragging)
  const handleDragOver = useCallback((currentIndex, option, groupIndex = null) => {
    const state = dragStateRef.current;
    
    if (!state.isDragging || state.lastIndex === null) return;
    
    // If this is the same index as start, ignore (not a drag yet)
    if (currentIndex === state.startIndex && !state.hasMoved) return;
    
    // Check group constraint (if not dragSelectAcrossGroups and we have groups)
    if (!dragSelectAcrossGroups && indexToGroupMap && state.startGroupIndex !== null) {
      const currentGroupIndex = indexToGroupMap[currentIndex];
      if (currentGroupIndex !== state.startGroupIndex) {
        // Outside the starting group, ignore this item
        return;
      }
    }
    
    // First movement - add the starting item to pending changes and optimistic state
    if (!state.hasMoved) {
      state.hasMoved = true;
      state.pendingChanges.push(state.startOption);
      // Add to optimistic selections for visual feedback
      setOptimisticSelections(prev => new Set([...prev, state.startOption.value]));
    }
    
    // Calculate range from lastIndex to currentIndex to handle fast dragging
    const start = Math.min(state.lastIndex, currentIndex);
    const end = Math.max(state.lastIndex, currentIndex);
    
    // Collect new values to add to optimistic selections
    const newOptimisticValues = [];
    
    // Process all items in the range
    for (let i = start; i <= end; i++) {
      if (state.processedIndices.has(i)) continue;
      
      // Check group constraint for each item in range
      if (!dragSelectAcrossGroups && indexToGroupMap && state.startGroupIndex !== null) {
        const itemGroupIndex = indexToGroupMap[i];
        if (itemGroupIndex !== state.startGroupIndex) {
          continue; // Skip items outside starting group
        }
      }
      
      const opt = flatOptions[i];
      if (!opt || opt.loading || opt.disabled) continue;
      
      const isSelected = isOptionSelected(opt);
      const shouldBeSelected = state.dragAction === 'select';
      
      if (shouldBeSelected !== isSelected) {
        state.pendingChanges.push(opt);
        newOptimisticValues.push(opt.value);
      }
      state.processedIndices.add(i);
    }
    
    // Update optimistic selections for visual feedback
    if (newOptimisticValues.length > 0) {
      setOptimisticSelections(prev => new Set([...prev, ...newOptimisticValues]));
    }
    
    state.lastIndex = currentIndex;
  }, [flatOptions, isOptionSelected, dragSelectAcrossGroups, indexToGroupMap]);

  // End drag operation and fire changes
  const handleDragEnd = useCallback(() => {
    const state = dragStateRef.current;
    
    if (state.isDragging) {
      if (state.hasMoved) {
        // It was a drag - batch fire all pending changes
        state.pendingChanges.forEach(opt => {
          parentOnChange(id, opt);
        });
      } else if (state.startOption) {
        // It was just a click (no movement) - toggle single item
        parentOnChange(id, state.startOption);
      }
    }
    
    // Clear optimistic selections - parent value will now reflect the actual state
    setOptimisticSelections(new Set());
    
    // Reset state
    dragStateRef.current = {
      isDragging: false,
      hasMoved: false,
      dragAction: null,
      processedIndices: new Set(),
      pendingChanges: [],
      lastIndex: null,
      startIndex: null,
      startOption: null,
      startGroupIndex: null
    };
  }, [parentOnChange, id]);

  // Memoized handler for item change (eliminates inline functions)
  const handleItemChange = useCallback((option) => {
    if (option.loading) return;
    parentOnChange(id, option);
    if (setSearchString) setSearchString("");
    if (setSearchBarFocus) setSearchBarFocus(true);
    if (setFocusSelectTagsInput) setFocusSelectTagsInput(true);
  }, [parentOnChange, id, setSearchString, setSearchBarFocus, setFocusSelectTagsInput]);

  // Global mouseup listener to end drag
  useEffect(() => {
    const handleMouseUp = () => handleDragEnd();
    document.addEventListener('mouseup', handleMouseUp);
    return () => document.removeEventListener('mouseup', handleMouseUp);
  }, [handleDragEnd]);

  return {
    optimisticSelections,
    handleDragStart,
    handleDragOver,
    handleItemChange
  };
};
